import { EventDetails } from '../types/event.types';

/**
 * Formats a Unix timestamp (seconds) to ISO 8601 format for Google Calendar
 * Format: YYYYMMDDTHHmmssZ (UTC)
 */
function formatGoogleCalendarDateTime(timestamp: number): string {
  const date = new Date(timestamp * 1000); // Convert seconds to milliseconds
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  const seconds = String(date.getUTCSeconds()).padStart(2, '0');
  
  return `${year}${month}${day}T${hours}${minutes}${seconds}Z`;
}

/**
 * Formats a Unix timestamp (seconds) to ISO 8601 format for Outlook
 * Format: YYYY-MM-DDTHH:mm:ss (UTC)
 */
function formatOutlookDateTime(timestamp: number): string {
  const date = new Date(timestamp * 1000); // Convert seconds to milliseconds
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  const seconds = String(date.getUTCSeconds()).padStart(2, '0');
  
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
}

/**
 * Formats a Unix timestamp (seconds) to ISO 8601 format for Yahoo Calendar
 * Format: YYYYMMDDTHHmmssZ (UTC)
 */
function formatYahooDateTime(timestamp: number): string {
  const date = new Date(timestamp * 1000); // Convert seconds to milliseconds
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  const seconds = String(date.getUTCSeconds()).padStart(2, '0');
  
  return `${year}${month}${day}T${hours}${minutes}${seconds}Z`;
}

/**
 * Truncates text to a maximum length, ensuring it doesn't break URLs
 * Only truncates if absolutely necessary to prevent URL length issues
 */
function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  // Only truncate if text is extremely long (to prevent URL length issues)
  // Most modern browsers support URLs up to 8000+ characters
  return text.substring(0, maxLength - 3) + '...';
}

/**
 * Generates a Google Calendar URL for adding an event
 * @param event Event details object
 * @param eventUrl The URL to the event page
 * @returns Google Calendar URL with pre-filled event data
 */
export function generateGoogleCalendarUrl(event: EventDetails, eventUrl: string): string {
  const title = event.eventTitle || event.title || 'Event';
  const location = event.isOnline === "1" 
    ? 'Online Event' 
    : (event.locationAddress || event.locationAddressDefinition || '');
  const startDate = formatGoogleCalendarDateTime(event.eventStartDateTime);
  const endDate = formatGoogleCalendarDateTime(event.eventEndDateTime);
  
  // Build description with event URL if available
  let fullDescription = event.description || '';
  if (eventUrl) {
    fullDescription = fullDescription 
      ? `${fullDescription}\n\n${eventUrl}`
      : eventUrl;
  }
  // Google Calendar can handle longer descriptions, only truncate if extremely long
  // URL encoding can make text 3x longer, so we allow up to 8000 chars before encoding
  const description = truncateText(fullDescription, 8000);
  
  // Google Calendar URL - don't use URLSearchParams to avoid double encoding
  const params = [
    'action=TEMPLATE',
    `text=${encodeURIComponent(title)}`,
    `dates=${startDate}/${endDate}`,
    `details=${encodeURIComponent(description)}`,
    `location=${encodeURIComponent(location)}`,
    'sf=true',
    'output=xml'
  ];
  
  return `https://calendar.google.com/calendar/render?${params.join('&')}`;
}

/**
 * Generates an Outlook.com calendar URL for adding an event
 * @param event Event details object
 * @param eventUrl The URL to the event page
 * @returns Outlook.com calendar URL with pre-filled event data
 */
export function generateOutlookCalendarUrl(event: EventDetails, eventUrl: string): string {
  const title = event.eventTitle || event.title || 'Event';
  const location = event.isOnline === "1" 
    ? 'Online Event' 
    : (event.locationAddress || event.locationAddressDefinition || '');
  const startDate = formatOutlookDateTime(event.eventStartDateTime);
  const endDate = formatOutlookDateTime(event.eventEndDateTime);
  
  // Build description with event URL if available
  let fullDescription = event.description || '';
  if (eventUrl) {
    fullDescription = fullDescription 
      ? `${fullDescription}\n\n${eventUrl}`
      : eventUrl;
  }
  // Outlook can handle longer descriptions, only truncate if extremely long
  const description = truncateText(fullDescription, 8000);
  
  // Outlook URL - don't use URLSearchParams to avoid double encoding
  const params = [
    `subject=${encodeURIComponent(title)}`,
    `startdt=${startDate}`,
    `enddt=${endDate}`,
    `body=${encodeURIComponent(description)}`,
    `location=${encodeURIComponent(location)}`
  ];
  
  return `https://outlook.live.com/calendar/0/deeplink/compose?${params.join('&')}`;
}

/**
 * Generates a Yahoo Calendar URL for adding an event
 * @param event Event details object
 * @param eventUrl The URL to the event page
 * @returns Yahoo Calendar URL with pre-filled event data
 */
export function generateYahooCalendarUrl(event: EventDetails, eventUrl: string): string {
  const title = event.eventTitle || event.title || 'Event';
  const location = event.isOnline === "1" 
    ? 'Online Event' 
    : (event.locationAddress || event.locationAddressDefinition || '');
  const startDate = formatYahooDateTime(event.eventStartDateTime);
  
  // Build description with event URL if available
  let fullDescription = event.description || '';
  if (eventUrl) {
    fullDescription = fullDescription 
      ? `${fullDescription}\n\n${eventUrl}`
      : eventUrl;
  }
  // Yahoo Calendar can handle longer descriptions, only truncate if extremely long
  const description = truncateText(fullDescription, 8000);
  const duration = calculateDuration(event.eventStartDateTime, event.eventEndDateTime);
  
  // Yahoo Calendar URL format - don't use URLSearchParams to avoid double encoding
  const params = [
    'v=60',
    'view=d',
    'type=20',
    `title=${encodeURIComponent(title)}`,
    `st=${startDate}`,
    `dur=${duration}`,
    `desc=${encodeURIComponent(description)}`,
    `in_loc=${encodeURIComponent(location)}`
  ];
  
  return `https://calendar.yahoo.com/?${params.join('&')}`;
}

/**
 * Calculates duration in hours for Yahoo Calendar
 * Yahoo expects duration in hours (decimal)
 */
function calculateDuration(startTimestamp: number, endTimestamp: number): string {
  const durationMs = (endTimestamp - startTimestamp) * 1000;
  const durationHours = durationMs / (1000 * 60 * 60);
  return durationHours.toString();
}

